import { useEffect, useRef } from "react"
import { t } from "@lingui/core/macro"
import { removeGhostDraft } from "../../components/messaging/DraftCache/DraftCache"
import {
    createLogMessage,
    messageToThread,
} from "../../components/messaging/MessagingContext"
import { isPersistentTippingActive } from "../../components/messaging/toggleUtils"
import { RoomType } from "../../components/messaging/types"
import { getFollowingUsernames } from "../../components/mobile_broadcast/api"
import { useAppContext } from "../../hooks/appContext"
import { error } from "../../utils/debug"
import { setFollowers, userFollowed, userUnfollowed } from "../followersSlice"
import { useAppDispatch } from "../hooks"
import {
    addIgnoreUser,
    removeIgnoreUser,
    useGetIgnoredUsersQuery,
} from "../ignoredUsersSlice"
import {
    addMessageToHistory,
    DEFAULT_THREAD_PARAMS,
    messagingApi,
    updateMessageReactions,
    updateProfileCache,
    updateThreadUserInfo,
    upsertThread,
} from "../messagingSlice"
import { store } from "../store"
import { setUserTyping } from "../typingIndicatorSlice"
import { updateTokenBalance } from "../userSlice"
import type {
    IIgnoreTopic,
    IMessageReactionUpdate,
    Interfaces,
    IPushUserTyping,
    ITipAlertTopic,
    Message,
    PushPrivateMessage,
    Thread,
    TokenBalanceUpdate,
    UserTopic,
} from "../../components/messaging/types"
import type {
    MobileBroadcastTopics,
    UserFollowerTopic,
} from "../../components/mobile_broadcast/types"
import type { IUserInfo } from "@multimediallc/web-utils/types"

const TYPING_INDICATOR_PERIOD_MS = 5000

type TokenUpdateProps = {
    userTokenUpdateTopic: MobileBroadcastTopics["UserTokenUpdateTopic"]
    uuid: string | undefined
}

type UserMessageProps = {
    userMessageTopic: UserTopic<PushPrivateMessage>
    interfaces: Interfaces
    uuid: string | undefined
}

type UserIgnoreProps = {
    userIgnoreTopic: UserTopic<IIgnoreTopic>
    interfaces: Interfaces
    uuid: string | undefined
}

type UserTipAlertProps = {
    userTipAlertTopic: UserTopic<ITipAlertTopic>
    uuid: string | undefined
}

type UserTypingProps = {
    userTypingTopic: UserTopic<IPushUserTyping>
    uuid: string | undefined
}

type UserMessageReactionProps = {
    userMessageReactionTopic: UserTopic<IMessageReactionUpdate>
    uuid: string | undefined
}

export const useTokenUpdateEffect = ({
    userTokenUpdateTopic,
    uuid,
}: TokenUpdateProps): void => {
    const dispatch = useAppDispatch()

    useEffect(() => {
        const dispatchTokenUpdate = (event: TokenBalanceUpdate) => {
            dispatch(updateTokenBalance(event.tokens))
        }

        let userTokenUpdateTopicInstance: InstanceType<
            typeof userTokenUpdateTopic
        >
        if (uuid !== undefined) {
            userTokenUpdateTopicInstance = new userTokenUpdateTopic(uuid)
            userTokenUpdateTopicInstance.onMessage.listen(dispatchTokenUpdate)
        }

        return () => {
            userTokenUpdateTopicInstance?.onMessage.removeListener(
                dispatchTokenUpdate,
            )
        }
    }, [uuid])
}

export const useUserTypingEffect = ({
    userTypingTopic,
    uuid,
}: UserTypingProps): void => {
    const dispatch = useAppDispatch()

    const timers = useRef<{ [username: string]: NodeJS.Timeout }>({})

    useEffect(() => {
        const handleTypingEvent = (typingEvent: IPushUserTyping) => {
            // starts a new timer that will clear the typing state after 5 seconds
            const typingUser = typingEvent.fromUsername

            dispatch(setUserTyping({ username: typingUser, isTyping: true }))

            // if a timer exists for this user, clear it
            if (timers.current[typingUser] !== undefined) {
                clearTimeout(timers.current[typingUser])
            }

            timers.current[typingUser] = setTimeout(() => {
                dispatch(
                    setUserTyping({
                        username: typingUser,
                        isTyping: false,
                    }),
                )
                delete timers.current[typingUser]
            }, TYPING_INDICATOR_PERIOD_MS)
        }

        if (
            uuid !== undefined &&
            userTypingTopic.onMessage.listenerCount() === 0
        ) {
            userTypingTopic.onMessage.listen(handleTypingEvent, false)
        }

        return () => {
            userTypingTopic.onMessage.removeListener(handleTypingEvent)
            Object.values(timers.current).forEach(clearTimeout)
        }
    }, [uuid, userTypingTopic, dispatch])
}

export const useUserMessageEffect = ({
    userMessageTopic,
    interfaces,
    uuid,
}: UserMessageProps): void => {
    const { context: appContext } = useAppContext()
    const dispatch = useAppDispatch()
    const { data: ignoredUsersData } = useGetIgnoredUsersQuery()

    useEffect(() => {
        const dispatchMessage = (m: PushPrivateMessage) => {
            if (m.room !== "") return // Only handle DMs
            const existingThread = messagingApi.endpoints.getThreads
                .select(DEFAULT_THREAD_PARAMS)(store.getState())
                .data?.threads.find(
                    (t: Thread) => t.other_user.username === m.otherUsername,
                )
            let otherUserProfile: IUserInfo | undefined
            if (existingThread === undefined) {
                otherUserProfile = messagingApi.endpoints.getProfile.select(
                    m.otherUsername,
                )(store.getState())?.data?.user
            }
            const thread = messageToThread(
                m,
                appContext.logged_in_user?.username,
                existingThread?.other_user || otherUserProfile,
            )
            const isFromMe =
                m.fromUser.username === appContext.logged_in_user?.username

            const isIgnored = ignoredUsersData?.users.includes(m.otherUsername)
            if (isIgnored && !isFromMe) return

            dispatch(upsertThread(thread, isFromMe))

            const otherUsername =
                m.otherUsername === appContext.logged_in_user?.username
                    ? m.fromUser.username
                    : m.otherUsername

            dispatch(
                setUserTyping({ username: otherUsername, isTyping: false }),
            )

            // Update the profile cache for the other user in case user color changed
            if (!isFromMe && m.fromUser) {
                const profileUpdateData: Partial<IUserInfo> = {
                    inFanclub: m.fromUser.inFanclub,
                    hasTokens: m.fromUser.hasTokens,
                    tippedRecently: m.fromUser.tippedRecently,
                    tippedALotRecently: m.fromUser.tippedALotRecently,
                    tippedTonsRecently: m.fromUser.tippedTonsRecently,
                    isMod: m.fromUser.isMod,
                }
                dispatch(updateProfileCache(otherUsername, profileUpdateData))
            }

            if (!isFromMe) {
                interfaces.dmUnreadData.addUnreadRecipient(otherUsername)
            }

            const cacheState = messagingApi.endpoints.getHistory.select({
                username: otherUsername,
            })(store.getState())

            if (cacheState.data) {
                const newMessage: Message = {
                    i: m.messageID,
                    m: m.message,
                    created_at: (m.createdAt?.getTime() ?? Date.now()) / 1000,
                    media: [],
                    other_user: m.otherUsername,
                    from_user: m.fromUser,
                    tip_amount: m.tipAmount,
                }
                dispatch(addMessageToHistory(otherUsername, newMessage))
            }
        }

        if (
            uuid !== undefined &&
            userMessageTopic.onMessage.listenerCount() === 0
        ) {
            userMessageTopic.onMessage.listen(dispatchMessage, false)
        }

        return () => {
            userMessageTopic.onMessage.removeListener(dispatchMessage)
        }
    }, [uuid, ignoredUsersData])
}

export const useIgnoreUserEffect = ({
    userIgnoreTopic,
    interfaces,
    uuid,
}: UserIgnoreProps): void => {
    const dispatch = useAppDispatch()

    useEffect(() => {
        const dispatchIgnoreUpdate = (update: IIgnoreTopic) => {
            const ignoredUsername = update.username
            let message: string

            if (update.isIgnored) {
                message = t`Ignoring ${ignoredUsername}`
                dispatch(addIgnoreUser(ignoredUsername))
                removeGhostDraft(ignoredUsername)
            } else {
                message = t`No longer ignoring ${ignoredUsername}`
                dispatch(removeIgnoreUser(ignoredUsername))
            }

            interfaces.dmUnreadData.refresh()

            dispatch(
                addMessageToHistory(
                    ignoredUsername,
                    createLogMessage(ignoredUsername, message),
                ),
            )

            dispatch(
                messagingApi.util.invalidateTags([
                    { type: "Threads", id: "LIST" },
                ]),
            )
        }

        if (
            uuid !== undefined &&
            userIgnoreTopic.onMessage.listenerCount() === 0
        ) {
            userIgnoreTopic.onMessage.listen(dispatchIgnoreUpdate)
        }

        return () => {
            userIgnoreTopic.onMessage.removeListener(dispatchIgnoreUpdate)
        }
    }, [uuid])
}

export const useTipAlertEffect = ({
    userTipAlertTopic,
    uuid,
}: UserTipAlertProps): void => {
    const { context: appContext } = useAppContext()
    const dispatch = useAppDispatch()
    const { data: ignoredUsersData } = useGetIgnoredUsersQuery()

    useEffect(() => {
        const dispatchTipAlert = (update: ITipAlertTopic) => {
            if (
                isPersistentTippingActive() &&
                update.roomType === RoomType.DM
            ) {
                // DM Tips alerts are handled through UserMessageTopic when PersistentTipping PERFORM toggle is enabled
                return
            }
            // Add temporary tip message to history if user is on conversation page
            const loggedInUsername = appContext.logged_in_user?.username
            const isFromMe = update.fromUser.username === loggedInUsername

            const otherUsername =
                update.toUsername === loggedInUsername
                    ? update.fromUser.username
                    : update.toUsername || update.fromUser.username

            // Update the profile and thread cache for the other user in case user color changed
            if (!isFromMe && update.fromUser) {
                const profileUpdateData: Partial<IUserInfo> = {
                    inFanclub: update.fromUser.inFanclub,
                    hasTokens: update.fromUser.hasTokens,
                    tippedRecently: update.fromUser.tippedRecently,
                    tippedALotRecently: update.fromUser.tippedALotRecently,
                    tippedTonsRecently: update.fromUser.tippedTonsRecently,
                    isMod: update.fromUser.isMod,
                }
                dispatch(
                    updateProfileCache(
                        update.fromUser.username,
                        profileUpdateData,
                    ),
                )
                dispatch(
                    updateThreadUserInfo(
                        update.fromUser.username,
                        profileUpdateData,
                    ),
                )
            }

            const cacheState = messagingApi.endpoints.getHistory.select({
                username: otherUsername,
            })(store.getState())
            const isIgnored = ignoredUsersData?.users.includes(otherUsername)

            if (cacheState.data && !isIgnored) {
                const tipId = update.tid
                if (tipId) {
                    // Check if a message with this specific ID already exists
                    // This prevents duplicates if the same tip event is processed again
                    // after an ignore/unignore cycle causes the listener to re-subscribe.
                    const messageExists = cacheState.data.messages.some(
                        (msg) => msg.i === tipId,
                    )

                    if (!messageExists) {
                        const newMessage: Message = {
                            i: tipId,
                            m: update.message || "",
                            created_at: (update.ts || Date.now()) / 1000,
                            media: [],
                            other_user: otherUsername,
                            from_user: update.fromUser,
                            tip_amount: update.amount,
                            is_temporary_tip_message: true,
                        }
                        dispatch(addMessageToHistory(otherUsername, newMessage))
                    }
                }
            }
        }

        if (
            uuid !== undefined &&
            userTipAlertTopic.onMessage.listenerCount() === 0
        ) {
            userTipAlertTopic.onMessage.listen(dispatchTipAlert)
        }

        return () => {
            userTipAlertTopic.onMessage.removeListener(dispatchTipAlert)
        }
    }, [uuid, appContext.logged_in_user?.username, ignoredUsersData])
}

export const useReactionUpdateEffect = ({
    userMessageReactionTopic,
    uuid,
}: UserMessageReactionProps) => {
    const dispatch = useAppDispatch()
    const dispatchReactionUpdate = (update: IMessageReactionUpdate) => {
        dispatch(
            updateMessageReactions(
                update.otherUsername,
                update.messageId,
                update.reactions,
            ),
        )
    }

    if (
        uuid !== undefined &&
        userMessageReactionTopic.onMessage.listenerCount() === 0
    ) {
        userMessageReactionTopic.onMessage.listen(dispatchReactionUpdate)
    }

    return () => {
        userMessageReactionTopic.onMessage.removeListener(
            dispatchReactionUpdate,
        )
    }
}

export const useFollowersEffect = (
    topics: MobileBroadcastTopics,
    roomUid: string | undefined,
): void => {
    const dispatch = useAppDispatch()
    const followersRequested = useRef<boolean>(false)
    useEffect(() => {
        if (!followersRequested.current) {
            getFollowingUsernames()
                .then((followingUsernames) => {
                    dispatch(setFollowers(followingUsernames))
                    followersRequested.current = true
                })
                .catch((e) => error(e))
        }
        const dispatchFollowerEvent = (event: UserFollowerTopic) => {
            // only dispatch a follow/unfollow event if its done by a viewer and not broadcaster
            if (event.followedUsername !== event.followerUsername) {
                if (event.isFollowing) {
                    dispatch(userFollowed(event.followerUsername))
                } else {
                    dispatch(userUnfollowed(event.followerUsername))
                }
            }
        }
        if (roomUid !== undefined) {
            const userFollowerTopic = new topics.UserFollowerTopic(roomUid)
            userFollowerTopic.onMessage.listen(dispatchFollowerEvent)
            return () =>
                userFollowerTopic.onMessage.removeListener(
                    dispatchFollowerEvent,
                )
        }
    }, [roomUid])
}
